import { useTranslation } from 'react-i18next';
import { Building2, FileText, Mail, Network } from 'lucide-react';
import { downloadDepartmentsTemplate } from '@/lib/excel';
import { anchorDisplayRows, useAnchorSeedRows } from '../useAnchorSeedRows';
import { isClearedAnchorDraft } from '../utils/flushBaselines';
import { parseDepartmentsFromExcel, type ParsedDepartment } from '../utils/excelParsers';
import type { DepartmentInput } from '../utils/schemas';
import { departmentDuplicateKeys, validateDepartmentRow } from '../utils/draftRowValidation';
import { upsertUploadedRows } from '@/lib/draft-rows/upsertUploadedRows';
import { mergeSetupDepartments } from '../utils/mergeSetupDepartments';
import type { DraftColumnDef } from '../components/EditableDraftTable';
import type { DraftRowShape } from '@/lib/draft-rows/draftRow';
import { DraftTableStep } from '../components/DraftTableStep';
import { SetupSourceChoice } from '../components/SetupSourceChoice';
import { usePersonioImport } from '../usePersonioImport';
import { useSeedOnOpen, type PersonioSeed } from '../useSeedOnOpen';
import { explainerFieldsFromColumns, explainerIcon, hashIcon } from '../components/FieldExplainer';

interface DepartmentsStepProps {
  departments: ParsedDepartment[];
  onDepartmentsChange: (departments: ParsedDepartment[]) => void;
  submitError?: string;
  /**
   * Step already imported — table is seeded from the import; on Weiter new
   * rows append, edits update in place and removals delete surgically
   * (ADR 0011, third amendment)
   */
  isDone?: boolean;
  /** The step's flush baseline — an empty draft with a surviving baseline is a cleared table */
  flushBaseline?: ParsedDepartment[];
  /** Personio rows that merge into the table when the step opens */
  personioSeed?: PersonioSeed<ParsedDepartment[]>;
}

const makeEmptyDepartmentRow = (rowNumber: number): DraftRowShape<DepartmentInput> => ({
  data: { name: '', abbreviation: '', managerEmail: '' },
  isValid: true,
  rowNumber
});

const DEPARTMENT_COLUMNS: DraftColumnDef<DepartmentInput>[] = [
  {
    field: 'name',
    headerKey: 'bulkOnboarding.excel.columns.departments.name',
    descriptionKey: 'initialSetup.columnHints.departments.name',
    required: true,
    explainer: {
      icon: explainerIcon(Building2),
      descriptionKey: 'initialSetup.fieldExplainer.fields.departments.name'
    }
  },
  {
    field: 'abbreviation',
    headerKey: 'bulkOnboarding.excel.columns.departments.abbreviation',
    descriptionKey: 'initialSetup.columnHints.departments.abbreviation',
    required: true,
    // A parent is referenced by its abbreviation (a brand-new in-batch parent
    // has no id yet, so the id-based reference used on later steps can't apply
    // here). When a row's Abkürzung is renamed, re-point sibling rows that
    // named the old one so they stay matched to the parent dropdown instead of
    // erroring.
    syncRowsAfterEdit: (rows, editedIndex, previousAbbr) => {
      const nextAbbr = rows[editedIndex]?.abbreviation;
      if (!previousAbbr || !nextAbbr || previousAbbr === nextAbbr) return rows;
      return rows.map((row, index) =>
        index !== editedIndex && row.parentAbbr === previousAbbr
          ? { ...row, parentAbbr: nextAbbr }
          : row
      );
    },
    explainer: {
      icon: hashIcon,
      descriptionKey: 'initialSetup.fieldExplainer.fields.departments.abbreviation'
    }
  },
  {
    field: 'description',
    headerKey: 'bulkOnboarding.excel.columns.departments.description',
    descriptionKey: 'initialSetup.columnHints.departments.description',
    optional: true,
    explainer: {
      icon: explainerIcon(FileText),
      descriptionKey: 'initialSetup.fieldExplainer.fields.departments.description'
    }
  },
  {
    field: 'parentAbbr',
    headerKey: 'bulkOnboarding.excel.columns.departments.parentAbbreviation',
    descriptionKey: 'initialSetup.columnHints.departments.parentAbbr',
    optional: true,
    explainer: {
      icon: explainerIcon(Network),
      descriptionKey: 'initialSetup.fieldExplainer.fields.departments.parentAbbr'
    }
  },
  {
    field: 'managerEmail',
    headerKey: 'bulkOnboarding.excel.columns.departments.managerEmail',
    descriptionKey: 'initialSetup.columnHints.departments.managerEmail',
    explainer: {
      icon: explainerIcon(Mail),
      descriptionKey: 'initialSetup.fieldExplainer.fields.departments.managerEmail'
    }
  }
];

export function DepartmentsStep({
  departments,
  onDepartmentsChange,
  submitError,
  isDone = false,
  flushBaseline,
  personioSeed
}: DepartmentsStepProps) {
  const { t } = useTranslation();
  const { departmentSeeds } = useAnchorSeedRows();

  const rows = anchorDisplayRows(departments, departmentSeeds, isDone, flushBaseline);

  // A parent is referenced by its abbreviation (a brand-new in-batch parent has
  // no id yet, so the id-based reference used on later steps can't apply here).
  // When an upload replaces a matched row under a new Abkürzung, re-point
  // sibling rows that named the old one so they stay matched to the parent
  // dropdown instead of dangling. Positional compare is valid because the
  // upload merge updates matched rows in place. Table edits are covered by the
  // abbreviation column's syncRowsAfterEdit.
  const repointRenamedParents = (prev: ParsedDepartment[], next: ParsedDepartment[]) => {
    let updated = next;
    prev.forEach((prevRow, index) => {
      const oldAbbr = prevRow.data.abbreviation;
      const newAbbr = next[index]?.data.abbreviation;
      if (oldAbbr && newAbbr && oldAbbr !== newAbbr) {
        updated = updated.map((sibling) =>
          sibling.data.parentAbbr === oldAbbr
            ? { ...sibling, data: { ...sibling.data, parentAbbr: newAbbr } }
            : sibling
        );
      }
    });
    return updated;
  };

  // A repeat import updates matching on-screen rows in place and appends
  // only new ones — re-uploading the same sheet must not stack duplicates.
  // An empty import must not touch state — it would materialize the seeds
  // into drafts and dirty the wizard without changing anything.
  const mergeImportedDepartments = (imported: ParsedDepartment[]) => {
    if (imported.length === 0) return;
    const merged = upsertUploadedRows(rows, imported, departmentDuplicateKeys);
    onDepartmentsChange(repointRenamedParents(rows, merged));
  };

  // An emptied table no longer shows Personio rows, whatever comes next.
  const handleRowsChange = (next: ParsedDepartment[]) => {
    if (next.length === 0) personioSeed?.onReplaced();
    onDepartmentsChange(next);
  };

  const personioImport = usePersonioImport({ onLoaded: personioSeed?.onLoaded });

  // Personio's departments carry generated abbreviations: they only fill gaps.
  const mergePersonioDepartments = (imported: ParsedDepartment[]) => {
    const merged = mergeSetupDepartments(rows, imported);
    if (merged !== rows) onDepartmentsChange(merged);
  };

  // Seeds into the table the admin sees, so autosave only ever saves rows
  // shown here. A done step takes only its own load: the stored rows would
  // bring back departments the admin removed or renamed.
  useSeedOnOpen({
    personioSeed,
    ready: !isDone || personioSeed?.ownLoad === true,
    seed: (imported) => {
      if (imported.length === 0) return false;
      mergePersonioDepartments(imported);
      return true;
    }
  });

  const handleParse = async (file: File) => {
    const result = await parseDepartmentsFromExcel(file);
    // Personio's employee export merges like a Personio read.
    if (result.personioExport) mergePersonioDepartments(result.departments);
    else mergeImportedDepartments(result.departments);
    if (result.departments.length > 0) personioSeed?.onReplaced();
    return result.departments.length;
  };

  const parentOptions = [...new Set(rows.map((row) => row.data.abbreviation).filter(Boolean))].map(
    (abbreviation) => ({ value: abbreviation, label: abbreviation })
  );

  const columns: DraftColumnDef<DepartmentInput>[] = DEPARTMENT_COLUMNS.map((column) =>
    column.field === 'parentAbbr'
      ? {
          ...column,
          // A department must not be offered as its own parent.
          options: (row: DraftRowShape<DepartmentInput>) =>
            parentOptions.filter((option) => option.value !== row.data.abbreviation)
        }
      : column
  );

  const explainerSections = [{ fields: explainerFieldsFromColumns(columns) }];
  // The empty state's two sources speak for themselves.
  const description =
    rows.length === 0
      ? undefined
      : t(
          personioSeed?.loaded
            ? 'initialSetup.personio.loaded'
            : 'bulkOnboarding.departments.description'
        );

  return (
    <>
      <DraftTableStep
        title={t('bulkOnboarding.departments.title')}
        description={description}
        isDone={isDone}
        doneNoticeKeys={{
          titleKey: 'initialSetup.doneNotice.departments.title',
          descriptionKey: 'initialSetup.doneNotice.departments.description'
        }}
        rows={rows}
        draftRows={departments}
        onRowsChange={handleRowsChange}
        clearScope={isDone ? 'all' : 'drafts'}
        pendingDeleteAll={isClearedAnchorDraft(departments, flushBaseline)}
        columns={columns}
        validateRow={validateDepartmentRow}
        duplicateChecks={departmentDuplicateKeys}
        makeEmptyRow={makeEmptyDepartmentRow}
        addLabelKey="initialSetup.addRow.department"
        titleKey="initialSetup.tables.departments"
        uploadTitle={t('bulkOnboarding.departments.uploadTitle')}
        explainerHeadingKey="initialSetup.fieldExplainer.headings.departments"
        explainerSections={explainerSections}
        downloadTemplate={downloadDepartmentsTemplate}
        parse={handleParse}
        importActions={personioImport.toolbarButton}
        submitError={submitError}
        emptyState={({ openUpload, addRow }) => (
          <SetupSourceChoice
            downloadTemplate={downloadDepartmentsTemplate}
            onUpload={openUpload}
            onAddManually={addRow}
            addLabelKey="initialSetup.addRow.department"
            explainerHeadingKey="initialSetup.fieldExplainer.headings.departments"
            explainerSections={explainerSections}
            personio={personioImport}
          />
        )}
      />
      {personioImport.dialogs}
    </>
  );
}
